const { getAxiosInstance } = require('../connection');
const encodeQueryParams = require('../encodeQueryParams');

/**
 * @typedef {Object} EmailTemplateResponse
 * @property {number} id
 * @property {string} template_name
 * @property {string} language - ISO language code, e.g. 'en'
 * @property {string} title - Email subject line
 * @property {string} body - HTML body
 * @property {string|null} from_email
 * @property {string|null} from_name
 * @property {string} template_type - Derived type: 'invite'|'leadscan'|'email'
 * @property {string|null} email_type - 'activation_invite'|'reminder'|'qr_only'|'export_leadscan'|'custom'
 * @property {string|null} event_type - 'in_person'|'online'|'community'
 * @property {string|null} target_group - 'attendee'|'speaker'|'exhibitor'|'exhibitor_leadscan'|'member'|'team'
 * @property {boolean|null} include_qr_code
 * @property {string|null} date_created - ISO datetime string
 * @property {string|null} date_updated - ISO datetime string
 */

/**
 * @typedef {Object} EmailTemplateWriteFields
 * @property {string} templateName
 * @property {string} subject - Email subject line
 * @property {string} body - HTML body (sanitized server-side)
 * @property {string} [fromEmail]
 * @property {string} [fromName]
 * @property {string} [emailType] - 'activation_invite'|'reminder'|'qr_only'|'export_leadscan'|'custom'
 * @property {string} [eventType] - 'in_person'|'online'|'community'
 * @property {string} [targetGroup] - 'attendee'|'speaker'|'exhibitor'|'exhibitor_leadscan'|'member'|'team'
 * @property {boolean} [includeQrCode=false]
 * @property {boolean} [includeInviteCodes=false]
 */

/**
 * @param {EmailTemplateWriteFields & { language?: string }} data
 */
function toEmailTemplateWriteData(data) {
    const { templateName, subject, body, fromEmail, fromName, language, emailType, eventType, targetGroup, includeQrCode, includeInviteCodes } = data;

    return {
        template_name: templateName,
        title: subject,
        body,
        from_email: fromEmail || undefined,
        from_name: fromName || undefined,
        language: language ?? undefined,
        email_type: emailType ?? undefined,
        event_type: eventType ?? undefined,
        target_group: targetGroup ?? undefined,
        include_qr_code: includeQrCode ?? undefined,
        include_invite_codes: includeInviteCodes ?? undefined,
    };
}

module.exports = {
    /**
     * Fetch valid composition parameter choices for an event.
     * Use the returned values to populate dropdowns before calling composeEmailTemplate.
     *
     * @param {number} eventId
     * @returns {Promise<{
     *   languages: string[],
     *   email_types: string[],
     *   event_types: { options: string[], fixed: boolean },
     *   target_groups: string[],
     *   include_qr_code: { available: boolean }
     * }>}
     */
    fetchEmailTemplateOptions: function (eventId) {
        return getAxiosInstance({
            url: '/events/' + encodeURIComponent(eventId) + '/email-templates/options',
            method: 'GET',
        }).then(response => response.data);
    },

    /**
     * Fetch all available email content blocks for an event.
     * Blocks are language-scoped and ordered; language defaults to 'en' on the server.
     *
     * @param {number} eventId
     * @returns {Promise<{
     *   blocks: Array<{
     *     order: number,
     *     block_type: string,
     *     text: string,
     *     email_types: string[],
     *     event_types: string[],
     *     target_groups: string[],
     *     when_qr_code_included: boolean|null
     *   }>
     * }>}
     */
    fetchEmailBlocks: function (eventId) {
        return getAxiosInstance({
            url: '/events/' + encodeURIComponent(eventId) + '/email-blocks',
            method: 'GET',
        }).then(response => response.data);
    },

    /**
     * Compose an email template body from blocks matching the given parameters.
     * Returns a preview with a suggested title and name — save it with createEmailTemplate.
     *
     * @param {number} eventId
     * @param {{
     *   language: string,
     *   email_type: 'activation_invite'|'reminder'|'qr_only'|'export_leadscan'|'custom',
     *   event_type: 'in_person'|'online'|'community',
     *   target_group: 'attendee'|'speaker'|'exhibitor'|'exhibitor_leadscan'|'member'|'team',
     *   include_qr_code?: boolean
     * }} data
     * @returns {Promise<{
     *   title: string,
     *   template_name: string,
     *   blocks: Array<{ order: number, block_type: string, text: string }>,
     *   body: string
     * }>}
     */
    composeEmailTemplate: function (eventId, data) {
        return getAxiosInstance({
            url: '/events/' + encodeURIComponent(eventId) + '/email-templates/compose',
            method: 'POST',
            data,
        }).then(response => response.data);
    },

    /**
     * Save a composed email template for an event.
     * The language is fixed at creation and cannot be changed later.
     *
     * @param {number} eventId
     * @param {EmailTemplateWriteFields & { language: string }} data
     * @returns {Promise<EmailTemplateResponse>}
     */
    createEmailTemplate: function (eventId, data) {
        return getAxiosInstance({
            url: '/events/' + encodeURIComponent(eventId) + '/email-templates',
            method: 'POST',
            data: toEmailTemplateWriteData(data),
        }).then(response => response.data);
    },

    /**
     * List all email templates for an event, optionally filtered.
     *
     * @param {number} eventId
     * @param {{ language?: string, email_type?: string }} [queryParams]
     * @returns {Promise<{ templates: EmailTemplateResponse[] }>}
     */
    fetchEmailTemplates: function (eventId, queryParams) {
        return getAxiosInstance({
            url: '/events/' + encodeURIComponent(eventId) + '/email-templates' + encodeQueryParams(queryParams),
            method: 'GET',
        }).then(response => response.data);
    },

    /**
     * Fetch a single email template by ID.
     *
     * @param {number} eventId
     * @param {number} templateId
     * @returns {Promise<EmailTemplateResponse>}
     */
    fetchEmailTemplate: function (eventId, templateId) {
        return getAxiosInstance({
            url: '/events/' + encodeURIComponent(eventId) + '/email-templates/' + encodeURIComponent(templateId),
            method: 'GET',
        }).then(response => response.data);
    },

    /**
     * Delete an email template.
     *
     * @param {number} eventId
     * @param {number} templateId
     * @returns {Promise<void>}
     */
    deleteEmailTemplate: function (eventId, templateId) {
        return getAxiosInstance({
            url: '/events/' + encodeURIComponent(eventId) + '/email-templates/' + encodeURIComponent(templateId),
            method: 'DELETE',
            data: {},
        }).then(response => response.data);
    },

    /**
     * Update an existing email template. Language cannot be changed after creation.
     *
     * @param {number} eventId
     * @param {number} templateId
     * @param {EmailTemplateWriteFields} data
     * @returns {Promise<EmailTemplateResponse>}
     */
    updateEmailTemplate: function (eventId, templateId, data) {
        return getAxiosInstance({
            url: '/events/' + encodeURIComponent(eventId) + '/email-templates/' + encodeURIComponent(templateId),
            method: 'PUT',
            data: toEmailTemplateWriteData(data),
        }).then(response => response.data);
    },
};
